import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { useFirebase } from '../contexts/FirebaseContext';
import { useFavorites } from '../contexts/FavoritesContext';
import { Heart } from 'lucide-react';
import { ChefHeader } from '../components/ios';

// Mirrors iOS MenuItemCard (MenuView.swift): ~160x100 rounded-12 image card with a
// black gradient at the bottom, footnote semibold name, green bold caption2 price,
// and a heart.toggle affordance. Tap navigates to the restaurant (like the iOS
// NavigationLink to RestaurantDetailView).
const FavoriteMenuItemCard = ({ item, restaurant, onOpen }) => {
  const { toggleFavorite } = useFavorites();

  // Truncate name to first two words if longer than 7 letters (mirrors iOS)
  const truncatedName = (() => {
    const name = item.name || '';
    const words = name.split(' ');
    if (name.length > 7 && words.length > 2) return words.slice(0, 2).join(' ');
    return name;
  })();

  const imageURL =
    item.menuItemImageURL || item.imageURL || item.restaurantBannerURL ||
    restaurant?.bannerURL || '/placeholder-food.jpg';

  return (
    <div
      onClick={onOpen}
      className="relative w-full h-[100px] rounded-xl overflow-hidden cursor-pointer shadow-[0_2px_5px_rgba(0,0,0,0.1)] ring-1 ring-black/10"
    >
      {/* Image — fit inside the card frame (no crop), blurred cover of the
          same image fills the letterbox so portrait uploads stay clean */}
      <div
        className="absolute inset-0 bg-cover bg-center blur-md scale-125 opacity-80"
        style={{ backgroundImage: `url(${imageURL})` }}
      />
      <img
        src={imageURL}
        alt={item.name}
        loading="lazy"
        className="absolute inset-0 w-full h-full object-contain"
      />

      {/* Dark gradient backdrop so text is always legible */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />

      {/* Overlay content — name, price + heart */}
      <div className="absolute inset-x-0 bottom-0 p-3">
        <h3
          className="text-[13px] font-semibold text-white truncate"
          style={{ textShadow: '0 1px 2px rgba(0,0,0,0.5)' }}
        >
          {truncatedName}
        </h3>
        <div className="flex items-center justify-between mt-0.5">
          <span
            className="text-[11px] font-bold text-[#34C759]"
            style={{ textShadow: '0 1px 0 rgba(0,0,0,0.8)' }}
          >
            ${item.price?.toFixed(2) || '0.00'}
          </span>
          <button
            onClick={(e) => {
              e.stopPropagation();
              toggleFavorite(item);
            }}
            className="p-0.5"
            aria-label="Remove from favorites"
          >
            <Heart
              size={16}
              className="text-[#FF3B30]"
              fill="#FF3B30"
              style={{ filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.5))' }}
            />
          </button>
        </div>
      </div>
    </div>
  );
};

function FavoritesPage() {
  const navigate = useNavigate();
  const { db } = useFirebase();
  const { favorites, loading, error } = useFavorites();
  const [restaurants, setRestaurants] = useState({});
  const [restaurantsLoaded, setRestaurantsLoaded] = useState(false);

  // Load restaurant data for favorites
  useEffect(() => {
    if (favorites.length > 0) {
      loadRestaurantsForFavorites();
    } else {
      setRestaurantsLoaded(true);
    }
  }, [favorites]);

  const loadRestaurantsForFavorites = async () => {
    try {
      const restaurantIds = [...new Set(favorites.map(item => item.restaurantId).filter(id => id != null))];
      const restaurantsData = {};

      for (const restaurantId of restaurantIds) {
        const q = query(
          collection(db, 'restaurants'),
          where('__name__', '==', restaurantId)
        );
        const snapshot = await getDocs(q);

        if (!snapshot.empty) {
          const doc = snapshot.docs[0];
          restaurantsData[restaurantId] = {
            id: doc.id,
            ...doc.data()
          };
        }
      }

      setRestaurants(restaurantsData);
    } catch (err) {
      console.error('Error loading restaurants:', err);
    } finally {
      setRestaurantsLoaded(true);
    }
  };

  const getRestaurantForItem = (item) => {
    const restaurant = restaurants[item.restaurantId];

    if (restaurant) {
      return restaurant;
    }

    // Placeholder restaurant (mirrors iOS fallback using the restaurant type)
    return {
      id: item.restaurantId || 'unknown',
      name: item.restaurantName || item.restaurantType || 'Restaurant',
      description: 'Favorite dish',
      address: '',
      type: item.restaurantType || 'Restaurant'
    };
  };

  const isLoading = loading || (favorites.length > 0 && !restaurantsLoaded);

  // Group favorites by restaurant name, sorted (mirrors iOS groupedFavorites)
  const groupedFavorites = favorites.reduce((groups, item) => {
    const restaurant = getRestaurantForItem(item);
    const restaurantName = restaurant.name || 'Unknown Restaurant';
    if (!groups[restaurantName]) {
      groups[restaurantName] = [];
    }
    groups[restaurantName].push({ item, restaurant });
    return groups;
  }, {});
  const sortedRestaurantNames = Object.keys(groupedFavorites).sort();

  return (
    <div className="min-h-screen bg-[#F2F2F7]">
      <ChefHeader title="Favorites" />

      {isLoading ? (
        <div className="flex items-center justify-center py-32">
          <div className="loading-spinner" />
        </div>
      ) : error ? (
        <div className="px-6 py-16 text-center">
          <p className="text-[15px] text-[#FF3B30]">{error}</p>
        </div>
      ) : favorites.length === 0 ? (
        /* EmptyStateView — headline title + subheadline secondary message */
        <div className="flex flex-col items-center justify-center px-6 py-32 space-y-4">
          <Heart size={48} className="text-[#8E8E93]" strokeWidth={1.5} />
          <h2 className="text-[17px] font-semibold text-gray-900">No Favorites Yet</h2>
          <p className="text-[15px] text-[#6D6D72] text-center">
            Items you favorite will appear here
          </p>
        </div>
      ) : (
        /* ScrollView — VStack(spacing: 24) of restaurant groups, top padding 16 */
        <div className="max-w-lg md:max-w-3xl lg:max-w-5xl mx-auto pt-4 pb-8 space-y-6">
          {sortedRestaurantNames.map((restaurantName) => {
            const items = groupedFavorites[restaurantName];
            return (
              <section key={restaurantName}>
                {/* Restaurant section header — title3 bold */}
                <h2 className="text-[20px] font-bold text-gray-900 px-[7px] mb-3">
                  {restaurantName}
                </h2>

                {/* 2-column grid — LazyVGrid with 12pt column / 16pt row spacing */}
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-3 gap-y-4 px-[7px]">
                  {items.map(({ item, restaurant }) => (
                    <div key={item.id} className="flex flex-col gap-1 min-w-0">
                      <FavoriteMenuItemCard
                        item={item}
                        restaurant={restaurant}
                        onOpen={() => navigate(`/dashboard/restaurant/${restaurant.id}`)}
                      />
                      {/* Category pill below card — caption2 secondary on gray/10 */}
                      <span className="self-start text-[11px] text-[#8E8E93] bg-black/[0.06] rounded px-1 py-0.5 mx-1">
                        {item.category}
                      </span>
                    </div>
                  ))}
                </div>
              </section>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default FavoritesPage;
